// Angular
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
// Lodash
import _isEqual from 'lodash-es/isEqual';
// Afidium
import {
    IGetBookingsAirResponse,
    IGetBookingsStayResponse,
    IGetBookingsVehicleRentalResponse,
    TGetBookingResponse
} from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { IMenuDetails } from 'libraries/hotel/components/menu-details/menu-details.component';
import { ILabels } from 'libraries/interfaces/ILabels';
import { Yii } from 'libraries/interfaces/Yii';
import { MenuDetailsService } from 'libraries/hotel/components/menu-details/services/menu-details.service';
import { CardComponent } from 'libraries/ui_card/card.component';
import { BookingResourceService } from "libraries/service/resources/booking/bookingResource.service";
import {
    ITableServices
} from "libraries/component/booking-information/components/sectors/components/shared/services/table-services/table-services.service";
import { NotepadComponent, NOTEPAD_EDITOR_PERMISSION } from '../../../notepad/notepad.component';
import { INotepad } from "libraries/service/resources/booking/interfaces/notepad.interface";
import { finalize } from "rxjs/operators";
import { NotificationType } from "libraries/interfaces/INotification";
import { PermissionResourceService } from 'libraries/service/resources/permission/permissionResource.service';

enum MenuTabs {
    invoicing = 'invoicing',
    note = 'note',
    remark = 'remark',
    osi = 'osi',
    vendorAlert = 'vendorAlert',
}

const items: { [key in MenuTabs]: IMenuDetails } = {
    invoicing: {
        code: MenuTabs.invoicing,
        name: Yii.t('wui-shared-js', 'Usage interne'),
        exist: true,
        selected: false
    },
    note: {
        code: MenuTabs.note,
        name: Yii.t('wui-shared-js', 'Notes'),
        exist: true,
        selected: true
    },
    remark: {
        code: MenuTabs.remark,
        name: Yii.t('wui-shared-js', 'Remarques'),
        exist: true,
        selected: false
    },
    osi: {
        code: MenuTabs.osi,
        name: Yii.t('wui-shared-js', 'OSI'),
        exist: true,
        selected: false
    },
    vendorAlert: {
        code: MenuTabs.vendorAlert,
        name: Yii.t('wui-shared-js', 'Alertes fournisseurs'),
        exist: true,
        selected: false
    }
};

type Booking = IGetBookingsAirResponse | IGetBookingsStayResponse | IGetBookingsVehicleRentalResponse;

@Component({
    selector: 'booking-remarks',
    template: `
        <card #cardContainer [isCollapsible]="true" class="collapsible">
            <div class="card-header-content">
                <div class="title" (click)="menuToggleCard()">
                    {{ wording.title }}
                </div>

                <div *ngIf="cardContainer.showContent" class="tabs-field">
                    <menu-details [items]="menuItems" (onChangeTab)="changeTab($event)"></menu-details>
                </div>

                <div class="editButton"
                     *ngIf="isEditingNotepad && selectedTab === 'note' && isEditable && !loading && !error">
                    <btn type="actionHeaderLink"
                         (click)="onCancel()"
                         [label]="editLabel.cancel">
                    </btn>
                    <btn type="actionPrincipal e2e-send-notes"
                         (click)="onValidate()"
                         [label]="editLabel.validate">
                    </btn>
                </div>

                <div class="editButton" *ngIf="selectedTab === 'vendorAlert' && !loading && !error">
                    <btn type="actionInlineLink e2e-order-vendor-alerts"
                         (click)="toggleSort()"
                         [label]="sortLabel.order + (vendorAlertsOrder ? sortLabel.descending : sortLabel.ascending)">
                    </btn>
                </div>
            </div>
            <div class="card-body-content">
                <div class="booking-data-content">
                    <loader *ngIf="loading" [message]="wording.applyingUpdate"></loader>
                    <notification *ngIf="error" [type]="errorNotificationType" (onClose)="error = null">
                        {{error.message}}
                    </notification>

                    <ng-container *ngIf="!error && !loading && menuItems.length">
                        <notepad #notepadComponent
                                 *ngIf="isMenuItem('note')"
                                 [hidden]="selectedTab !== 'note'"
                                 [displayMode]="'CRUD'"
                                 [notes]="bookingData.note"
                                 [isEditable]="isEditable"
                                 [isEditPermission]="isNotepadPermission"
                                 [isLengthRestriction]="true"
                                 (onEdit)="isEditingNotepad = true"
                                 (onDestroy)="isEditingNotepad = false"></notepad>
                        <ng-container *ngIf="isMenuItem('osi')">
                            <osi *ngFor="let data of bookingData['osi'];"
                                 [hidden]="selectedTab !== 'osi'" [osi]="data"></osi>
                        </ng-container>
                        <ng-container *ngIf="isMenuItem('invoicing')">
                            <remark *ngFor="let data of bookingData['invoicing'];"
                                    [hidden]="selectedTab !== 'invoicing'"
                                    [remark]="data"></remark>
                        </ng-container>
                        <ng-container *ngIf="isMenuItem('remark')">
                            <remark *ngFor="let data of bookingData['remark'];"
                                    [hidden]="selectedTab !== 'remark'"
                                    [remark]="data"></remark>
                        </ng-container>
                        <vendor-alerts *ngIf="isMenuItem('vendorAlert')"
                                       [hidden]="selectedTab !== 'vendorAlert'"
                                       [vendorAlerts]="bookingData.vendorAlert"
                                       [vendorAlertsOrder]="vendorAlertsOrder"></vendor-alerts>
                    </ng-container>

                    <ng-container *ngIf="!error && !loading && (!menuItems || !menuItems.length)">
                        <div class="noInformation">
                            {{ wording.noRemark }}
                        </div>
                    </ng-container>
                </div>
            </div>
        </card>
    `,
    styleUrls: ['../card.scss'],
    styles: [`
        remark:nth-child(even) ::ng-deep .col-right,
        osi:nth-child(even) ::ng-deep .col-right {
            background-color: #f2f2f2;
        }

        remark:nth-child(odd) ::ng-deep .col-right,
        osi:nth-child(odd) ::ng-deep .col-right {
            background-color: #f9fafc;
        }

        .noInformation {
            padding: .75rem;
            color: #6E6E6E;
        }
    `]
})
export class BookingRemarksComponent implements OnChanges {
    @ViewChild('cardContainer')
    public cardContainer: CardComponent;

    @ViewChild('notepadComponent')
    public notepadComponent: NotepadComponent;

    @Input()
    public bookingData: Booking;

    @Input()
    public tableServices: ITableServices;

    @Input()
    public isEditable: boolean = true;

    @Output()
    public readonly onCardTitleClick: EventEmitter<string> = new EventEmitter();

    @Output()
    public readonly onBookingRefreshed: EventEmitter<TGetBookingResponse> = new EventEmitter();

    public isNotepadPermission: boolean;

    public menuItems: IMenuDetails[] = [];

    public readonly editLabel: { cancel: string, validate: string } = {
        cancel: Yii.t('wui-shared-js', 'Abandonner'),
        validate: Yii.t('wui-shared-js', 'Valider')
    };

    public readonly sortLabel: { descending: string, ascending: string, order: string } = {
        descending: Yii.t('wui-shared-js', 'Décroissant'),
        ascending: Yii.t('wui-shared-js', 'Croissant'),
        order: Yii.t('wui-shared-js', 'Ordre : '),
    };

    /** true during notepad edition, false otherwise. */
    public isEditingNotepad: boolean = false;

    /** true is vendorAlert are on descending ordered, false otherwise. */
    public vendorAlertsOrder: boolean = true;

    public readonly wording: ILabels = {
        title: Yii.t('wui-shared-js', 'Remarques'),
        noRemark: Yii.t('wui-shared-js', 'Aucune remarque sur ce dossier'),
        applyingUpdate: Yii.t('wui-shared-js', 'Application des modifications...'),
    };

    public selectedTab: MenuTabs = null;

    public readonly name: string = 'bookingRemarks';

    public readonly errorNotificationType: NotificationType = NotificationType.Error;

    public loading: boolean = false;

    public error: Error = null;

    constructor(
        private readonly menuDetailsService: MenuDetailsService,
        private readonly bookingResourceService: BookingResourceService,
        private readonly permissionResource: PermissionResourceService,
    ) {
    }

    public ngOnChanges(simpleChanges: SimpleChanges): void {
        this.permissionResource.hasPermission(NOTEPAD_EDITOR_PERMISSION).subscribe({
            next: (isPermitted: boolean): void => {
                this.isNotepadPermission = isPermitted;
            }
        });

        const {
            previousValue,
            currentValue
        }: { previousValue: Booking, currentValue: Booking } = simpleChanges.bookingData;
        if (!_isEqual(previousValue, currentValue)) {
            this.menuItems = Object.keys(items)
                .filter((item: MenuTabs) => (item === MenuTabs.note && this.isEditable) || Boolean(this.bookingData[item]))
                .map((item: MenuTabs) => items[item])
                .map((item: IMenuDetails, index: number) => ({
                    ...item,
                    selected: this.selectedTab ? item.code === this.selectedTab.toString() : index === 0
                }));
            if (!this.selectedTab && this.menuItems.length > 0) {
                this.selectedTab = MenuTabs[this.menuItems[0].code];
            }
        }
    }

    public changeTab(code: string): void {
        this.menuItems = this.menuDetailsService.changeTab(this.menuItems, code);
        this.selectedTab = MenuTabs[code];
    }

    public menuToggleCard(): void {
        this.onCardTitleClick.emit(this.name);
    }

    public onCancel(): void {
        this.notepadComponent.cancelEdition();
        this.isEditingNotepad = false;
    }

    public toggleSort(): void {
        this.vendorAlertsOrder = !this.vendorAlertsOrder;
    }

    public onValidate(): void {
        const notes: INotepad[] = this.notepadComponent.extractNotes();

        this.loading = true;

        this.bookingResourceService.updateNotes(this.bookingData.syscode, notes)
            .subscribe(
                (value: IGetBookingsAirResponse) => {
                    this.isEditingNotepad = false;
                    setTimeout(() => {
                        this.onBookingRefreshed.emit(value);
                        this.loading = false;
                    }, 2000);
                },
                (error: Error) => {
                    this.error = error;
                    this.loading = false;
                }
            );
    }


    public isMenuItem(code: string): boolean {
        return Boolean(this.menuItems.find((menuItem: IMenuDetails): boolean => menuItem.code === code));
    }
}
